import { z, type ZodType } from 'zod'
import { normaliseResponseKeys } from '../caseNormalise'
import type { VfsClientFilePart } from '../vfsclient/formData'
import { vfsClientRequest } from '../vfsclient/vfsClient'

/**
 * The §4.8 posture, in one place.
 *
 * Guide §4.8 routes are VFS's backend calling DHA on our behalf: same transport and
 * same token as every other VFS call, but DHA's rules apply to what travels inside.
 * Those rules are uppercase-on-submit, camelCase-on-read and an `?idNo=` the backend
 * needs for its own audit row — three things that are wrong for VFS's native routes
 * and mandatory for these.
 *
 * A wrapper rather than three arguments repeated at twenty call sites, because the
 * failure mode of forgetting one is silent. An un-uppercased surname is accepted by
 * DHA and stored, and surfaces months later as a name printed wrong on a card.
 */
export interface DhaRequestOptions<TResponse> {
  path: string
  method?: 'GET' | 'POST'
  /**
   * The applicant's ID number, sent as `?idNo=`.
   *
   * Required by every §4.8 route except eligibility and initiate (which carry it in
   * the body), the static reference data (which takes no parameters) and
   * open-applications (which takes it as a path segment).
   *
   * An argument rather than a session-store read: `lib/` may not import `features/`
   * — the ESLint boundary config allows `lib` only `lib` and `config` — which is
   * what keeps this transport a function of its arguments rather than of whichever
   * screen happened to call it.
   */
  idNo?: string
  body?: unknown
  parse?: ZodType<TResponse>
  signal?: AbortSignal
  /**
   * Extra keys exempt from the uppercase transform, merged with the base64 defaults.
   *
   * Forwarded to `vfsClientRequest` with the rest of the options. It was declared here
   * and consumed by nobody until 2026-09-08 — `deepUppercase` was called with no
   * options — so §4.9's wording was uppercased despite the call site asking for it
   * verbatim. An option that silently does nothing is worse than no option.
   */
  preserveKeys?: readonly string[]
  /**
   * Binary parts, forwarded to `vfsClientRequest` — see the warning there about why a Blob
   * may not travel inside `body`.
   *
   * Declared rather than left to the spread below, on the same reasoning as
   * `preserveKeys` above: an option you can pass and have silently ignored is worse
   * than no option, and `preserveKeys` spent a fortnight in exactly that state.
   */
  files?: readonly VfsClientFilePart[]
  timeoutMs?: number
}

export function dhaRequest<TResponse = void>(
  options: DhaRequestOptions<TResponse>
): Promise<TResponse> {
  const { idNo, parse, ...rest } = options

  return vfsClientRequest<TResponse>({
    ...rest,
    query: idNo === undefined ? undefined : { idNo },
    uppercase: true,
    parse: parse ? normalised(parse) : undefined,
  })
}

/**
 * Runs `normaliseResponseKeys` over the envelope's `data` before the DTO sees it.
 *
 * The spec documents every DHA response in PascalCase and the gateway answers in
 * lowerCamel; §4.8 passes DHA's answer through, so the disagreement passes through
 * with it. Keeping the transform is not belt-and-braces — every DTO here is a
 * `looseObject` whose arrays are `.nullish()`, so a PascalCase payload does NOT fail
 * to parse. It parses to an empty list and the screen renders nothing, which is
 * exactly how the declaration screen shipped blank until 2026-08-25 (see the header
 * of `features/static-data/api/staticData.api.ts`).
 *
 * Scoped to `data` on the success path deliberately: the envelope's own
 * `message`/`errorCode`/`errors` are read before this runs, and DHA's
 * `validation_failures` map — which `caseNormalise.ts` warns must never be rewritten
 * — lives only in error bodies, which never reach here.
 */
function normalised<TResponse>(parse: ZodType<TResponse>): ZodType<TResponse> {
  return z.preprocess(normaliseResponseKeys, parse)
}
